/**
 * Investor / customer email+password auth context.
 *
 * Kept separate from the admin AuthContext so admin login flows are untouched.
 * Token is persisted in localStorage under `investorToken`.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'investorToken';

const InvestorAuthContext = createContext(null);

export const useInvestorAuth = () => {
  const ctx = useContext(InvestorAuthContext);
  if (!ctx) throw new Error('useInvestorAuth must be used within InvestorAuthProvider');
  return ctx;
};

export const InvestorAuthProvider = ({ children }) => {
  const [investor, setInvestor] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (token) => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvestor({
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
      });
    } catch (_err) {
      localStorage.removeItem(TOKEN_KEY);
      setInvestor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) fetchMe(token);
    else setLoading(false);
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setInvestor(data.customer);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Login failed. Please try again.' };
    }
  };

  const register = async ({ name, email, password, phone, otp }) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password, phone, otp });
      localStorage.setItem(TOKEN_KEY, data.token);
      setInvestor(data.customer);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Registration failed. Please try again.' };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Could not send reset link. Please try again.' };
    }
  };

  const resetPassword = async ({ token, new_password }) => {
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password });
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Could not reset password. The link may have expired.' };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setInvestor(null);
  };

  const getInvestorAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <InvestorAuthContext.Provider
      value={{
        investor,
        loading,
        login,
        register,
        forgotPassword,
        resetPassword,
        logout,
        getInvestorAuthHeaders,
      }}
    >
      {children}
    </InvestorAuthContext.Provider>
  );
};
